import React from 'react';
import { CARD, TEXT_MUTED, TEXT_PRIMARY, TONES } from './tokens';

/**
 * KPI card.
 *
 * ActiveCalls and CallHistory each rendered four of these fully unrolled —
 * two 57-line blocks that differed only in the label and icon — while
 * VoiceDashboard drove its own from an array with a different visual
 * (rounded-xl, icon above the number). This settles on the dashboard's
 * treatment, which was the better of the two.
 *
 * `trend` is optional and rendered as a subdued caption, so screens can show
 * a comparison without inventing another layout for it.
 */
const StatCard = ({ label, value, icon: Icon, tone = 'neutral', trend, onClick }) => {
  const t = TONES[tone] || TONES.neutral;
  const interactive = typeof onClick === 'function';

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`${CARD} p-4 ${interactive ? 'cursor-pointer hover:border-gray-300 dark:hover:border-white/[0.14] transition-colors' : ''}`}
    >
      {Icon && (
        <div className={`inline-flex p-2 rounded-lg mb-3 ${t.chip}`}>
          <Icon className="w-4 h-4" />
        </div>
      )}
      <p className={`text-2xl font-semibold tabular-nums leading-none ${TEXT_PRIMARY}`}>
        {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
      </p>
      <p className={`text-xs mt-1.5 ${TEXT_MUTED}`}>{label}</p>
      {trend && <p className={`text-[11px] mt-1 ${TEXT_MUTED}`}>{trend}</p>}
    </div>
  );
};

/** Responsive grid for a row of StatCards. */
export const StatGrid = ({ children, cols = 4 }) => {
  const map = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 lg:grid-cols-5',
  };
  return <div className={`grid ${map[cols] || map[4]} gap-3`}>{children}</div>;
};

export default StatCard;
